import React, { useEffect, useRef, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import axios from '../../axios';
import { useLanguage } from '../../i18n/LanguageContext';
import { getSpanishAliasBasename } from '../../utils/spanishMarket';
import copy from '../../i18n/bodexPlus.json';
import './BodexPlus.css';

export const priceLabel = (currency) => currency === 'eur' ? '29 €' : `29 ${currency.toUpperCase()}`;

function useOffer() {
  const { language } = useLanguage();
  const [offer, setOffer] = useState(null);
  useEffect(() => {
    let active = true;
    axios.get('/api/bodex-plus/offer', { params: { source_domain: window.location.hostname, lang: language } })
      .then(({ data }) => { if (active) setOffer(data); })
      .catch(() => { if (active) setOffer({ enabled: false }); });
    return () => { active = false; };
  }, [language]);
  return { offer, c: copy[language] || copy.en, language };
}

function Benefits({ c, currency }) {
  return <>
    <span className="bodex-plus-brand">Bodex+</span>
    <h2>{c.headline}</h2>
    <ul>{c.benefits.map(benefit => <li key={benefit}>{benefit}</li>)}</ul>
    <p className="bodex-plus-price">{c.monthly.replace('{price}', priceLabel(currency))}</p>
    <p className="bodex-plus-cancel">{c.cancel}</p>
  </>;
}

export function BodexPlusOffer({ address = '', email = '' }) {
  const { offer, c, language } = useOffer();
  if (!offer?.enabled) return null;
  return <section className="bodex-plus-card" aria-label="Bodex+">
    <Benefits c={c} currency={offer.currency} />
    <Link className="bodex-plus-button" target="_blank" rel="noopener" to={{ pathname: '/bodex-plus', search: `?lang=${language}`, hash: email ? `#${new URLSearchParams({ email })}` : '', state: { address, email } }}>{c.cta}</Link>
  </section>;
}

export default function BodexPlusPage() {
  const { offer, c, language } = useOffer();
  const location = useLocation();
  const history = useHistory();
  const [email, setEmail] = useState(() => location.state?.email || new URLSearchParams(location.hash.slice(1)).get('email') || '');
  const [name, setName] = useState('');
  const [address, setAddress] = useState(location.state?.address || '');
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const attempt = useRef(null);
  const checkoutState = new URLSearchParams(location.search).get('checkout');
  // Email fragments are prefill hints only; remove them after reading to limit URL exposure.
  useEffect(() => {
    if (new URLSearchParams(location.hash.slice(1)).has('email')) {
      history.replace({ ...location, hash: '', state: { ...location.state, email } });
    }
  }, [history, location, email]);
  const submit = async event => {
    event.preventDefault();
    if (busy || !accepted) return;
    setBusy(true); setError('');
    try {
      if (!attempt.current) attempt.current = window.crypto.randomUUID();
      const { data } = await axios.post('/api/bodex-plus/checkout', {
        email, name, address, language, accepted, attempt_id: attempt.current,
        prefix: getSpanishAliasBasename()
      });
      const url = new URL(data.url);
      if (url.protocol !== 'https:' || url.hostname !== 'checkout.stripe.com') throw new Error('Invalid checkout URL');
      window.location.assign(url.href);
    } catch (err) {
      if (err.response?.status === 409) attempt.current = null;
      setError(c.error); setBusy(false);
    }
  };
  return <main className="bodex-plus-page">
    <section className="bodex-plus-card" aria-label="Bodex+">
      {!offer ? <p role="status">{c.loading}</p> : !offer.enabled ? <p>{c.unavailable}</p> : <>
        <Benefits c={c} currency={offer.currency} />
        {checkoutState === 'success' ? <p role="status">{c.success}</p> : <>
          {checkoutState === 'cancelled' && <p role="status">{c.cancelled}</p>}
          <form onSubmit={submit} className="bodex-plus-form">
            <label>{c.email}<input type="email" autoComplete="email" maxLength={254} required value={email} onChange={e => { setEmail(e.target.value); attempt.current = null; }} /></label>
            <label>{c.name} <small>({c.optional})</small><input autoComplete="name" maxLength={200} value={name} onChange={e => setName(e.target.value)} /></label>
            <label>{c.address} <small>({c.optional})</small><input autoComplete="street-address" maxLength={500} value={address} onChange={e => setAddress(e.target.value)} /></label>
            <p>{c.terms.replace('{price}', priceLabel(offer.currency))}</p>
            <label className="bodex-plus-consent"><input type="checkbox" required checked={accepted} onChange={e => setAccepted(e.target.checked)} /><span>{c.accept}</span></label>
            <p className="bodex-plus-links"><Link to="/terms">{c.legal}</Link> · <Link to="/privacy">{c.privacy}</Link></p>
            {error && <p role="alert">{error}</p>}
            <button className="bodex-plus-button" type="submit" disabled={busy || !accepted}>{busy ? c.loading : c.checkout}</button>
          </form>
        </>}
      </>}
      {offer?.portal_url && <p><a href={offer.portal_url}>{c.manage}</a></p>}
      <p><Link to="/">{c.back}</Link></p>
    </section>
  </main>;
}
